'use client';
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Fragment, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IconButton } from '../../buttons';

export interface DrawerModalProps {
  title?: string;
  description?: React.ReactNode;
  position?: 'left' | 'right';
  isOpen: boolean;
  children: any;

  onClose(): void;

  unmount?: boolean;
  hideHeader?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  closeOnEscape?: boolean;
}

export function DrawerModal({
  title,
  description,
  position = 'right',
  isOpen,
  children,
  onClose,
  unmount,
  hideHeader = false,
  className = '',
  closeOnEscape,
}: DrawerModalProps) {
  const handleKeyDown = (event: globalThis.KeyboardEvent) => {
    event.code === 'Escape' && onClose();
  };
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeOnEscape]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <Transition.Root show={isOpen} as={Fragment} unmount={unmount}>
      <div className="fixed inset-0 overflow-hidden z-50" onClick={onClose}>
        <div className={`absolute inset-0 overflow-hidden`}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-70"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-70"
            leaveTo="opacity-0"
            unmount={unmount}
          >
            <div className="absolute inset-0 h-screen w-screen bg-neutral-70 opacity-50" />
          </Transition.Child>
          <div
            onClick={(event) => event.stopPropagation()}
            className={`
              fixed inset-y-0 max-w-full flex items-end
              ${position === 'left' ? 'left-0' : 'right-0'}
              ${className}
          `}
          >
            <Transition.Child
              as={Fragment}
              unmount={unmount}
              enter="transform transition ease-in-out duration-150"
              enterFrom={
                position === 'right' ? 'translate-x-full' : '-translate-x-full'
              }
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo={
                position === 'right' ? 'translate-x-full' : '-translate-x-full'
              }
            >
              <div className="w-screen h-2/3 md:h-screen md:w-[calc(100vw-4rem)] md:max-w-[450px]">
                <div className="h-full flex flex-col bg-white shadow-xl rounded-t-lg md:rounded-t-none md:rounded-l-lg">
                  <div className="h-full grid grid-rows-[min-content_1fr]">
                    {!hideHeader && title && (
                      <div className="p-4 md:p-6 border-b border-neutral-40">
                        <div className="flex items-center justify-between">
                          <h3 className="hd-md -mb-1.5">{title}</h3>
                          <div className="h-7 w-7 flex items-center">
                            <IconButton
                              size="md"
                              onClick={onClose}
                              variant="ghost-muted"
                            >
                              <span className="sr-only">Lukk</span>
                              <XMarkIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </IconButton>
                          </div>
                        </div>
                        <div className="mt-1">{description}</div>
                      </div>
                    )}
                    {!hideHeader && !title && (
                      <div className="flex justify-end items-center min-h-[4rem]">
                        <IconButton
                          size="md"
                          onClick={onClose}
                          className="mr-5"
                          variant="ghost-muted"
                        >
                          <span className="sr-only">Lukk</span>
                          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                        </IconButton>
                      </div>
                    )}
                    <div className="h-full flex-1 overflow-hidden overflow-y-scroll scrollbar-hide">
                      {children}
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </div>
    </Transition.Root>
  );
}

export default DrawerModal;
